import {
  saveTargetAudienceId,
  useChatCurrentGptModel,
  useIsChatEmpty,
  useStoredActiveConversationId,
  useStoredBrandVoiceId,
  useStoredTargetAudienceId,
} from 'features/aiWriter/AiWriterSidebar/steps/chat/chatStore';
import { useResetConversationMutation } from 'features/aiWriter/AiWriterSidebar/steps/chat/useResetConversationMutation';
import { useWarnAboutChatReset } from 'features/aiWriter/AiWriterSidebar/steps/chat/useShowChatResetModal';
import { useUpdateDocumentTargetAudience } from 'features/aiWriter/AiWriterSidebar/steps/chat/useUpdateDocumentTargetAudience';
import { ChatTargetAudienceSelect } from 'features/aiWriter/chat/ChatTargetAudienceSelect';
import { useGetProjectOrPreselectedLanguageAndCountry } from 'features/aiWriter/hooks/useGetProjectOrPreselectedLanguageAndCountry';
import { getActiveTab } from 'features/aiWriter/store/selectors';
import { TargetAudienceOption } from 'features/target-audience/components/AutocompleteDropdownMenuContent';
import { MouseEvent as ReactMouseEvent, ReactElement } from 'react';
import { useAppSelector } from 'store/hooks';

type ProjectTargetAudienceSelectorProps = {
  menuOpeningDirection?: 'top' | 'bottom' | 'right';
  customTrigger?: (props: {
    onClick: (event: ReactMouseEvent) => void;
    selectedOption: TargetAudienceOption | null;
    open: boolean;
  }) => ReactElement;
};

export const ProjectTargetAudienceSelector = ({
  menuOpeningDirection,
  customTrigger,
}: ProjectTargetAudienceSelectorProps = {}) => {
  const { language, country } = useGetProjectOrPreselectedLanguageAndCountry();
  const warnAboutChatReset = useWarnAboutChatReset();
  const isChatEmpty = useIsChatEmpty();
  const activeConversationId = useStoredActiveConversationId();

  const chatStoredTargetAudienceId = useStoredTargetAudienceId();
  const projectStoredTargetAudienceId =
    useAppSelector(getActiveTab).generateTextConfig.targetAudienceId;

  const targetAudienceId = chatStoredTargetAudienceId ?? projectStoredTargetAudienceId ?? undefined;

  const brandVoiceId = useStoredBrandVoiceId();
  const gptModel = useChatCurrentGptModel();

  const { mutateAsync: resetConversation } = useResetConversationMutation();
  const updateDocumentTargetAudience = useUpdateDocumentTargetAudience();

  const handleTargetAudienceChange = (targetAudienceId: string | null) => {
    if (activeConversationId && !isChatEmpty) {
      warnAboutChatReset({
        onAccept: () => {
          saveTargetAudienceId(targetAudienceId);
          updateDocumentTargetAudience({ targetAudienceId });
          resetConversation({ brandVoiceId, gptModel, targetAudienceId });
        },
      });
    } else {
      saveTargetAudienceId(targetAudienceId);
      updateDocumentTargetAudience({ targetAudienceId });
      resetConversation({ brandVoiceId, gptModel, targetAudienceId });
    }
  };

  return (
    <ChatTargetAudienceSelect
      targetLanguage={language}
      targetCountry={country}
      menuOpeningDirection={menuOpeningDirection || 'top'}
      customTrigger={customTrigger}
      onTargetAudienceIdChange={handleTargetAudienceChange}
      selectedTargetAudienceId={targetAudienceId}
    />
  );
};
